// OtpVerification.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { LockOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

/**
 * OtpVerification
 * - Robust 6-digit OTP inputs (keyboard nav + paste support)
 * - Countdown timer and resend attempts (max 3)
 * - Verifies OTP via API and stores received real token + user info
 * - Redirects based on role
 *
 * Required env: REACT_APP_API_URL
 */
const OtpVerification = () => {
  const navigate = useNavigate();

  // initial checks
  const token = localStorage.getItem("tempToken");
  const roleFromStorage = localStorage.getItem("role");

  // If user already has a role/token, redirect accordingly
  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    if (roleFromStorage) {
      // if role exists we probably already verified; redirect
      if (roleFromStorage === "ADMIN") navigate("/admin-home", { replace: true });
      else navigate("/user-home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // OTP input state (6 single-char boxes)
  const OTP_LENGTH = 6;
  const [otpArr, setOtpArr] = useState(Array(OTP_LENGTH).fill(""));
  const inputsRef = useRef([]);

  // UI / control state
  const [timeLeft, setTimeLeft] = useState(30);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // isMounted guard to avoid state updates after unmount
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // countdown timer
  useEffect(() => {
    setResendDisabled(timeLeft > 0);
    if (timeLeft <= 0) return undefined;

    const id = setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [timeLeft]);

  useEffect(() => {
    if (resendAttempts >= 3) {
      setResendDisabled(true);
      setTimeLeft(0);
    }
  }, [resendAttempts]);

  const focusInput = (idx) => {
    const el = inputsRef.current[idx];
    if (el) el.focus();
  };

  // single-digit change handler
  const handleChange = (value, idx) => {
    if (!/^\d*$/.test(value)) return; // only numbers
    const next = [...otpArr];
    next[idx] = value.slice(-1); // keep single char
    setOtpArr(next);
    if (value && idx < OTP_LENGTH - 1) focusInput(idx + 1);
  };

  // keyboard handling (backspace + arrow nav)
  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...otpArr];
      if (next[idx]) {
        next[idx] = "";
        setOtpArr(next);
        return;
      }
      if (idx > 0) {
        next[idx - 1] = "";
        setOtpArr(next);
        focusInput(idx - 1);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      focusInput(idx - 1);
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      e.preventDefault();
      focusInput(idx + 1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerifyOtp();
    }
  };

  // paste support: accept up to 6 digits
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text") || "";
    const digits = pasted.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (digits.length === 0) return;
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setOtpArr(next);
    const focusIndex = digits.length >= OTP_LENGTH ? OTP_LENGTH - 1 : digits.length;
    focusInput(focusIndex);
  };

  const otpString = otpArr.join("");

 const [errorMessage, setErrorMessage] = useState("");

// Replace handleVerifyOtp with this:
const handleVerifyOtp = async () => {
  setErrorMessage("");

  if (otpString.length !== OTP_LENGTH || !/^\d+$/.test(otpString)) {
    toast.error("Enter a valid 6-digit OTP");
    return;
  }
  if (!token) {
    setErrorMessage("Missing temporary token. Please login again.");
    toast.error("Missing temporary token. Please login again.");
    navigate("/login", { replace: true });
    return;
  }

  setSubmitting(true);
  try {
    // console.log("Verifying OTP...", { apiUrl: process.env.REACT_APP_API_URL, otp: otpString, token });

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/verify-otp`,
      { token, otp: otpString },
      { timeout: 15000 }
    );

    // console.log("verify response:", res?.status, res?.data);

    // defensive checks
    if (!res || !res.data) {
      throw new Error("Invalid response from server");
    }

    if (res.data.success) {
      // guard: make sure data object exists
      const profile = res.data.data || {};
      if (!res.data.realToken) {
        console.warn("realToken missing in response", res.data);
      }

      // store safely
      if (res.data.realToken) localStorage.setItem("token", res.data.realToken);
      if (profile.Role) localStorage.setItem("role", profile.Role);
      if (profile.Email) localStorage.setItem("email", profile.Email);
      if (profile.FullName) localStorage.setItem("fullName", profile.FullName);

      toast.success("OTP verified successfully!");

      // ensure navigation executes even if role missing
      setTimeout(() => {
        const roleLocal = localStorage.getItem("role");
        if (roleLocal === "ADMIN") navigate("/admin-home", { replace: true });
        else navigate("/user-home", { replace: true });
      }, 700);
    } else {
      // server returned success:false
      const msg = res.data.message || "OTP verification failed";
      setErrorMessage(msg);
      toast.error(msg);
      // console.warn("OTP verify failed:", res.data);
    }
  } catch (err) {
    // show helpful messages for common cases
    // console.error("OTP verify error:", err);
    const serverMsg = err?.response?.data?.message;
    if (serverMsg) setErrorMessage(serverMsg);

    if (err?.message && err.message.includes("timeout")) {
      setErrorMessage("Request timed out. Please try again.");
      toast.error("Request timed out. Please try again.");
    } else if (err?.response && err.response.status === 401) {
     
      toast.error("Wrong OTP Entered, Please check entered OTP.");
     
    } 
  } finally {
    // ALWAYS reset submitting so UI won't get stuck
    setSubmitting(false);
  }
};

// Replace handleResendOtp with this:
const handleResendOtp = async () => {
  setErrorMessage("");
  if (!token) {
    setErrorMessage("Missing token. Please login again.");
    toast.error("Missing token. Please login again.");
    navigate("/login", { replace: true });
    return;
  }
  if (resendAttempts >= 3) {
    // setErrorMessage("Maximum resend attempts reached.");
    toast.error("Maximum resend attempts reached");
    return;
  }

  try {
    setResendDisabled(true);
    // console.log("Resend OTP request", { apiUrl: process.env.REACT_APP_API_URL, token });

    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/resend-otp`,
      { token },
      { timeout: 12000 }
    );

    // console.log("resend response:", res?.status, res?.data);

    if (res?.data?.success) {
      toast.success("OTP resent. Check your email.");
      setResendAttempts((s) => s + 1);
      setTimeLeft(30);
    } else {
      const msg = res?.data?.message || "Failed to resend OTP";
      setErrorMessage(msg);
      toast.error(msg);
      // allow a short retry
      setTimeLeft(6);
    }
  } catch (err) {
    // console.error("Resend OTP error:", err);
    const serverMsg = err?.response?.data?.message;
    setErrorMessage(serverMsg || "Couldn't resend OTP right now");
    toast.error(serverMsg || "Couldn't resend OTP right now");
    setTimeLeft(6);
  } finally {
    // ensure UI consistent
    setResendDisabled(timeLeft > 0);
  }
};

  return (
    <div className="flex items-center justify-center min-h-screen p-6 bg-gradient-to-br from-yellow-50 via-purple-50 to-sky-50">
       <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              className="!mb-6 !ml-4"
              toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
            />

      <div className="w-full max-w-xl p-6 border shadow-2xl bg-white/95 backdrop-blur-sm border-white/30 rounded-2xl">
        <div className="flex items-center gap-4">
          <div className="p-3 text-blue-600 rounded-lg shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <LockOutlined style={{ fontSize: 18 }} />
          </div>
          <div>
            <h2 className="text-xl font-semibold sm:text-2xl text-slate-800">🔐 Enter OTP</h2>
            <p className="text-sm text-slate-500">We've sent a 6-digit code to your registered email.</p>
          </div>
        </div>

        <div
          className="p-4 mt-6 border rounded-md bg-slate-800 border-slate-200"
          onPaste={handlePaste}
          aria-label="OTP input group"
        >
          <label className="sr-only">Enter 6-digit OTP</label>
          <div className="flex justify-center gap-3">
            {otpArr.map((val, idx) => (
              <input
                key={idx}
                ref={(el) => (inputsRef.current[idx] = el)}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={val}
                onChange={(e) => handleChange(e.target.value, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                className="w-12 h-12 text-lg font-semibold text-center transition border border-transparent rounded-md shadow-sm sm:w-14 sm:h-14 sm:text-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label={`Digit ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 mt-6 sm:grid-cols-2">
          <button
            onClick={handleVerifyOtp}
            disabled={submitting}
            className="flex items-center justify-center gap-2 w-full py-2 font-medium text-white rounded-lg shadow-md transform transition hover:-translate-y-0.5 disabled:opacity-60 bg-gradient-to-r from-blue-600 to-indigo-600"
          >
            {submitting ? (
              <>
                <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </button>

          {/* after the Verify button
{errorMessage && (
  <p className="mt-2 text-sm text-center text-red-600">{errorMessage}</p>
)} */}

          <div className="flex flex-col items-center gap-2 sm:items-end">
            <button
              onClick={handleResendOtp}
              disabled={resendDisabled || resendAttempts >= 3}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-sm transition ${
                resendAttempts >= 3 ? "bg-slate-300 text-slate-600 cursor-not-allowed" : "bg-red-500 text-white hover:bg-red-600"
              }`}
            >
              <ReloadOutlined />
              Resend OTP
            </button>

            <div className="mt-1 text-xs text-slate-500">
              {resendAttempts >= 3 ? (
                <span>Attempts exhausted. Contact support.</span>
              ) : resendDisabled ? (
                <div className="flex items-center gap-2">
                  <span>⏳ Resend in</span>
                  <strong className="text-slate-700">{timeLeft}s</strong>
                  <div className="w-24 h-1 ml-3 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-1 bg-blue-500"
                      style={{ width: `${((30 - timeLeft) / 30) * 100}%` }}
                    />
                  </div>
                </div>
              ) : (
                <span className="text-slate-600">You can resend now ({resendAttempts}/3)</span>
              )}
            </div>
          </div>
        </div>

        <p className="mt-4 text-xs text-center text-slate-400">
          Attempt <span className="font-medium text-slate-600">{resendAttempts}</span> of 3
        </p>
      </div>
    </div>
  );
};

export default OtpVerification;
